import { axios, transformAxiosError } from "../utils/axios.create";
import {
  AppointmentsResponse,
  GetAppointmentsParams,
  AppointmentMeetingTypeEnum,
  AppointmentStatusEnum,
  AppointmentTypeEnum,
  Appointment,
} from "@/domain/appointment.interface";

export interface AppointmentReasonLanguage {
  language: string;
  name: string;
  description?: string;
}

export interface AppointmentReasonCategory {
  _id: string;
  languages: AppointmentReasonLanguage[];
  status: string;
}

export interface AppointmentReasonResponse {
  _id: string;
  categories: AppointmentReasonCategory[];
}

const getParticipantAppointments = async ({
  participantId,
  pageNumber = 0,
  pageSize = 5,
  sortField = "startDate",
  sortOrder = "asc",
  filter = "all",
}: GetAppointmentsParams): Promise<AppointmentsResponse> => {
  try {
    const queryParams = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
      sortField,
      sortOrder: sortOrder === "desc" ? "-1" : "1", // Convert to MongoDB format
      filter,
    });

    const response = await axios.get(
      `/participants/${participantId}/appointments?${queryParams.toString()}`
    );

    return response.data.body;
  } catch (error) {
    throw transformAxiosError(error as Error);
  }
};

const getAppointmentReasons = async (
  accountId: string,
  userId: string
): Promise<AppointmentReasonResponse[]> => {
  try {
    const response = await axios.get(
      `/participants/${userId}/appointment-reasons`
    );
    return response.data.body;
  } catch (error) {
    throw transformAxiosError(error as Error);
  }
};

export interface CreateAppointmentPayload {
  subject: string;
  appointmentType: AppointmentTypeEnum;
  meetingType: AppointmentMeetingTypeEnum;
  startDate: string; // ISO string
  endDate: string; // ISO string
  status: AppointmentStatusEnum;
  programId: string;
  participantsId: string[];
  organizerId: string;
  treatmentAgencyId?: string;
  locationId?: string;
  contactName?: string;
  appointmentReasonId?: string;
}

const createAppointment = async ({
  enrollmentId,
  userId,
  payload,
}: {
  enrollmentId: string;
  userId: string;
  payload: CreateAppointmentPayload;
}): Promise<Appointment> => {
  try {
    const response = await axios.post(
      `/participants/${userId}/enrollments/${enrollmentId}/appointments`,
      payload
    );
    return response.data.body;
  } catch (error) {
    throw transformAxiosError(error as Error);
  }
};

const updateAppointment = async ({
  enrollmentId,
  appointmentId,
  userId,
  payload,
}: {
  enrollmentId: string;
  appointmentId: string;
  userId: string;
  payload: Partial<CreateAppointmentPayload>;
}): Promise<Appointment> => {
  try {
    const response = await axios.patch(
      `/participants/${userId}/enrollments/${enrollmentId}/appointments/${appointmentId}`,
      payload
    );
    return response.data.body;
  } catch (error) {
    throw transformAxiosError(error as Error);
  }
};

const AppointmentsService = {
  getParticipantAppointments,
  getAppointmentReasons,
  createAppointment,
  updateAppointment,
};

export default AppointmentsService;
